/**
 * One error type for every way the backend (and the dev proxy) can fail.
 * The server has at least six error body shapes (see the rgs-api-contract skill);
 * this normalises them into a stable `code` plus user-safe `messages`. Raw SQL and
 * other internals stay in `raw` for "copy details", never in `messages`.
 */
export type ApiErrorCode =
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'analytics_unavailable'
  | 'dev_write_blocked'
  | 'contract'
  | 'network'
  | 'server'
  | 'unknown';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly messages: string[];
  readonly raw: unknown;

  constructor(init: { status: number; code: ApiErrorCode; messages: string[]; raw?: unknown }) {
    super(init.messages[0] ?? init.code);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.messages = init.messages;
    this.raw = init.raw;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

const SQL_HINTS: Array<[RegExp, string]> = [
  [/duplicate entry/i, 'A record with these values already exists.'],
  [/foreign key constraint|cannot delete or update a parent row/i, 'Other records still depend on this one.'],
  [
    /ER_[A-Z_]+|\bSELECT\s|\bINSERT\s+INTO\b|\bUPDATE\s+`|\bDELETE\s+FROM\b|EntityPropertyNotFound|QueryFailedError/,
    'The server rejected the request.',
  ],
];

/** Replace database internals with a readable sentence; keep ordinary messages as they are. */
export function safeMessage(message: string): string {
  for (const [pattern, friendly] of SQL_HINTS) if (pattern.test(message)) return friendly;
  return message;
}

function codeFor(status: number, body: Record<string, unknown>): ApiErrorCode {
  const error = typeof body.error === 'string' ? body.error : '';
  if (error === 'analytics_unavailable') return 'analytics_unavailable';
  if (error === 'dev_write_blocked') return 'dev_write_blocked';
  if (status === 400 || status === 422) return 'validation';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 503 && error) return 'server';
  if (status >= 500) return 'server';
  return 'unknown';
}

const DEFAULT_MESSAGE: Record<ApiErrorCode, string> = {
  validation: 'Some values were not accepted.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: 'You do not have permission to do this.',
  not_found: 'Not found.',
  conflict: 'This conflicts with an existing record.',
  analytics_unavailable: 'Analytics is not available in this environment.',
  dev_write_blocked: 'Writes to the shared dev backend are disabled in local development.',
  contract: 'The server sent a response this app does not understand.',
  network: 'The server could not be reached.',
  server: 'The server had a problem. Please try again.',
  unknown: 'Something went wrong.',
};

export function toApiError(status: number, body: unknown): ApiError {
  if (typeof body === 'string') {
    const code = codeFor(status, {});
    return new ApiError({ status, code, messages: [safeMessage(body.trim() || DEFAULT_MESSAGE[code])], raw: body });
  }
  const obj = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
  const code = codeFor(status, obj);
  const raw = obj.message;
  const list = Array.isArray(raw)
    ? raw.filter((m): m is string => typeof m === 'string')
    : typeof raw === 'string'
      ? [raw]
      : [];
  // 5xx bodies are internals; the auth and analytics codes have fixed wording.
  const useServerText = list.length > 0 && status < 500 && code !== 'unauthorized' && code !== 'analytics_unavailable';
  const messages = useServerText ? list.map(safeMessage) : [DEFAULT_MESSAGE[code]];
  return new ApiError({ status, code, messages, raw: body });
}

export const networkError = (cause: unknown) =>
  new ApiError({ status: 0, code: 'network', messages: [DEFAULT_MESSAGE.network], raw: cause });

export const contractError = (issues: unknown, raw: unknown) =>
  new ApiError({ status: 200, code: 'contract', messages: [DEFAULT_MESSAGE.contract], raw: { issues, raw } });
